import { useEffect, useRef, useState } from 'react';

import { Button } from 'components/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from 'components/Dialog';
import { Textarea } from 'components/Textarea';
import { Flex } from 'components/layout/Flex';
import { Typography } from 'components/typography';
import {
  ClonePromptGroup,
  CloneSavedInstructions,
  getLoadCloneInstructions,
  saveLoadCloneInstructions,
} from 'lib/api/cloneLoads';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

interface InstructionsDialogProps {
  open: boolean;
  sourceLoadId: number;
  emailId: number;
  startError?: ApiError;
  onOpenChange: (open: boolean) => void;
  onStart: () => Promise<boolean>;
  retry?: boolean;
}

export default function InstructionsDialog({
  open,
  sourceLoadId,
  emailId,
  startError,
  onOpenChange,
  onStart,
  retry = false,
}: InstructionsDialogProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [saved, setSaved] = useState<Maybe<CloneSavedInstructions>>(null);
  const [error, setError] = useState<Maybe<string>>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [didSave, setDidSave] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSaved(null);
    setError(null);
    setDidSave(false);
    if (open) {
      void getLoadCloneInstructions({ sourceLoadId, emailId }).then(
        (result) => {
          if (cancelled) return;
          if (result.isOk()) setSaved(result.value);
          else setError('Unable to load saved instructions.');
        }
      );
    }
    return () => {
      cancelled = true;
    };
  }, [open, sourceLoadId, emailId, attempt]);

  const submitLabel = retry ? 'Save and retry' : 'Save and clone';
  const customer = saved?.entries.find(
    (entry) => entry.group === ClonePromptGroup.Customer
  );

  const submit = async () => {
    if (isSubmitting || !saved || !customer) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await saveLoadCloneInstructions(
        { sourceLoadId, emailId },
        { ...saved, entries: [customer] }
      );
      if (result.isErr()) {
        setError(result.error.message);
        return;
      }
      setSaved(result.value);
      setDidSave(true);
      if (await onStart()) onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isSubmitting) onOpenChange(next);
      }}
    >
      <DialogContent
        className='flex max-w-sm overflow-hidden gap-0'
        style={{ maxHeight: 'calc(100dvh - 32px)' }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <form
          className='flex min-h-0 w-full flex-col'
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className='shrink-0 p-4 pb-3 pr-10'>
            <DialogTitle ref={titleRef} tabIndex={-1} className='outline-none'>
              Customer instructions
            </DialogTitle>
            <DialogDescription className='mt-1.5 text-xs leading-4'>
              Tell Kit how to read this customer’s emails.
              {customer && (
                <>
                  {' '}
                  Saved for future clones for{' '}
                  {customer.label || 'this customer'}.
                </>
              )}
            </DialogDescription>
          </div>
          <Flex
            direction='col'
            align='stretch'
            gap='sm'
            className='min-h-0 overflow-y-auto px-4 pb-4'
          >
            {!saved && !error && (
              <Typography variant='body-sm' role='status'>
                Loading saved instructions...
              </Typography>
            )}
            {customer && (
              <>
                <Textarea
                  aria-label='Customer instructions'
                  value={customer.text}
                  disabled={isSubmitting}
                  maxLength={32000}
                  placeholder="Example: Treat all times in this customer's emails as AM unless PM is specified."
                  rows={8}
                  className='h-48 min-h-0 resize-none overflow-y-auto leading-5'
                  onChange={(event) => {
                    const text = event.target.value;
                    setDidSave(false);
                    setSaved(
                      (current) =>
                        current && {
                          ...current,
                          entries: [{ ...customer, text }],
                        }
                    );
                  }}
                />
              </>
            )}
            {saved && !customer && (
              <Typography variant='body-sm' role='status'>
                This load has no linked customer. Choose a load with a customer
                to save instructions.
              </Typography>
            )}
            {error && (
              <div>
                <Typography
                  variant='body-xs'
                  role='alert'
                  className='text-error-600'
                >
                  {error}
                </Typography>
                <Button
                  buttonNamePosthog={null}
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={isSubmitting}
                  onClick={() => setAttempt((value) => value + 1)}
                >
                  <Typography
                    variant='body-sm'
                    weight='medium'
                    className='text-inherit'
                  >
                    {saved ? 'Discard edits and reload' : 'Retry'}
                  </Typography>
                </Button>
              </div>
            )}
            {startError && (
              <Typography
                variant='body-xs'
                role='alert'
                className='text-error-600'
              >
                {startError.message}
              </Typography>
            )}
            {didSave && (
              <Typography variant='body-xs' role='status'>
                Instructions saved. Cloning hasn’t started.
              </Typography>
            )}
          </Flex>
          <Flex
            gap='sm'
            justify='end'
            className='shrink-0 border-t border-neutral-200 px-4 py-3'
          >
            <Button
              buttonNamePosthog={null}
              type='button'
              variant='outline'
              size='sm'
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
            >
              <Typography
                variant='body-sm'
                weight='medium'
                className='text-inherit'
              >
                Cancel
              </Typography>
            </Button>
            <Button
              buttonNamePosthog={ButtonNamePosthog.CloneLoad}
              type='submit'
              size='sm'
              disabled={isSubmitting || !customer}
            >
              <Typography
                variant='body-sm'
                weight='medium'
                className='text-inherit'
              >
                {isSubmitting ? 'Saving and starting...' : submitLabel}
              </Typography>
            </Button>
          </Flex>
        </form>
      </DialogContent>
    </Dialog>
  );
}
